import React from "react";
import { Link } from "react-router-dom";
import { Brain, Microscope, Waypoints, ShieldCheck, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const PILLARS = [
  {
    icon: Brain,
    title: "Nerve-first approach",
    desc: "Neurosurgeons operate on the brain, spinal cord and peripheral nerves as their core work. That everyday familiarity with delicate neural tissue carries naturally into spine surgery.",
  },
  {
    icon: Microscope,
    title: "Familiar with the microscope & endoscope",
    desc: "The operating microscope and the endoscope are standard tools of neurosurgical training, which is why most endoscopic and minimally invasive spine surgery is performed with these techniques.",
  },
  {
    icon: Waypoints,
    title: "The brain–spine continuum",
    desc: "Conditions that involve both the brain and spine — cervical myelopathy, spinal tumours, complex cord problems — benefit from a single specialist trained across the entire neuraxis, without handovers.",
  },
];

const ChoiceOfSpecialist = () => {
  return (
    <section
      id="specialist-perspective"
      data-testid="specialist-perspective"
      className="relative bg-slate-50 py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-[0.9fr_1.1fr] gap-14">
        {/* Left copy */}
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Why a neurosurgeon</p>
          <h2 className="font-heading font-extrabold text-[#0b2c4d] text-4xl md:text-5xl tracking-tight mt-3 leading-tight">
            For spine surgery, a neurosurgeon is the natural choice
          </h2>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            The spine is, above all, a nerve structure — it houses the spinal cord and the nerves
            that control the body. Because a neurosurgeon&rsquo;s core training is the safe
            handling of nerves and the spinal cord, most modern minimally invasive spine procedures
            sit at the centre of their day-to-day practice.
          </p>
          <p className="mt-4 text-slate-500 leading-relaxed">
            For conditions such as slipped disc, sciatica, cervical disc disease, lumbar canal
            stenosis and spinal tumours, the operation is fundamentally about decompressing and
            protecting nerves. A neurosurgeon-led approach brings a nerve-first mindset to every
            step — from your first evaluation to recovery.
          </p>

          <Link
            to="/neurologist-vs-neurosurgeon"
            data-testid="neurologist-vs-neurosurgeon-link"
            className="mt-4 inline-flex items-center gap-1 text-brand font-semibold underline underline-offset-4 decoration-brand/40 hover:decoration-brand transition-colors"
          >
            Neurologist or neurosurgeon? Which doctor to see
            <ArrowUpRight className="w-4 h-4" />
          </Link>

          <div className="mt-8 flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5">
            <ShieldCheck className="w-5 h-5 text-brand shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600 leading-relaxed">
              Orthopedic spine surgeons bring valuable expertise, particularly in spinal
              deformity. For most conditions Dr. Reddy treats, where nerves and the spinal cord are
              the central concern, a neurosurgeon-led plan is our recommendation — made openly,
              together with you.
            </p>
          </div>
        </Reveal>

        {/* Right pillars — no reveal wrapper so all three are always visible */}
        <div className="grid grid-cols-1 gap-5 self-center">
          {PILLARS.map((p) => (
            <div
              key={p.title}
              data-testid={`specialist-pillar-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="glass-card rounded-2xl p-6 flex gap-5 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-xl bg-brand-light border border-brand/20 flex items-center justify-center shrink-0">
                <p.icon className="w-6 h-6 text-brand" />
              </div>
              <div>
                <h3 className="font-heading font-extrabold text-[#0b2c4d] text-lg tracking-tight">{p.title}</h3>
                <p className="mt-2 text-slate-500 text-sm leading-relaxed">{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ChoiceOfSpecialist;
